import {
  ControlPosition,
  Map,
  MapControl,
  useMap,
} from "@vis.gl/react-google-maps"
import { FC, memo, useEffect, useMemo, useRef } from "react"
import { CommonMarker } from "src/app/components"
import { useAppDispatch, useAppSelector } from "src/app/hooks"
import {
  addPostalCodeStyle,
  drawingModeSelector,
} from "src/app/slices/dispatcherRoutePlan.slice"
import {
  appConstants,
  center,
  mapContainerStyle,
} from "../../shared-module/constants"
import { useDrawingManager } from "../hooks"
import { RoutePlanningModel } from "../models"
import { UndoRedoControl } from "./UndoRedoMapControl"

export const RoutePlanningMap: FC = memo(() => {
  const routeIds = useAppSelector(
    (state) => state.routePlanning.routeList.map((e) => e.id),
    (prev, next) => {
      if (prev.length !== next.length) return false
      return prev.every((id, index) => id === next[index])
    },
  )
  return (
    <Map
      mapId={appConstants.googleMapId}
      style={mapContainerStyle}
      defaultCenter={center}
      disableDefaultUI={true}
      fullscreenControl={true}
      defaultZoom={3}
      maxZoom={20}
      minZoom={2}
      gestureHandling={"greedy"}
      renderingType="VECTOR"
    >
      <DrawingManager />
      <RouteMapStyleConfig />
      <RouteMapBounds />
      {routeIds.map((routeId) => {
        return <RouteMapDetail routeId={routeId} />
      })}
    </Map>
  )
})

const RouteMapDetail: FC<{ routeId: number }> = ({ routeId }) => {
  const route = useAppSelector((state) =>
    state.routePlanning.routeList.find((e) => e.id === routeId),
  ) as RoutePlanningModel
  return (
    <>
      {route.bounds.map((bound, index) => {
        return (
          <div key={"b" + index}>
            {bound.values.map((value, vIndex) => {
              return (
                <HighlightedRegionArea
                  key={"bv-" + index + vIndex}
                  color={route.color}
                  postalCode={(value as { code: string }).code}
                />
              )
            })}
          </div>
        )
      })}
    </>
  )
}

const HighlightedRegionArea = memo(({ postalCode, color }: any) => {
  const map = useMap()
  const dispatch = useAppDispatch()

  const featureStyleOptions = useMemo<google.maps.FeatureStyleOptions>(
    () => ({
      strokeColor: color,
      strokeOpacity: 1.0,
      strokeWeight: 1.5,
      fillColor: color,
      fillOpacity: 0.1,
    }),
    [color],
  )

  const postalCodeDetail = useAppSelector((state) => {
    return state.routePlanning.postalCodeStyles?.[postalCode]
  })

  useEffect(() => {
    if (!map || !google) return

    if (postalCodeDetail) {
      dispatch(
        addPostalCodeStyle({
          postalCode,
          placeId: postalCodeDetail.placeId,
          style: featureStyleOptions,
          location: {
            lat: postalCodeDetail.location?.lat,
            lng: postalCodeDetail.location?.lng,
          },
        }),
      )
      return
    }

    const findPlaceId = async () => {
      const { Place } = (await google.maps.importLibrary(
        "places",
      )) as google.maps.PlacesLibrary

      const { places } = await Place.searchByText({
        textQuery: postalCode + "",
        fields: ["id", "location"],
        includedType: "postal_code",
        locationBias: center,
      })

      if (!places.length) return

      const place = places[0]

      dispatch(
        addPostalCodeStyle({
          postalCode,
          placeId: place.id,
          style: featureStyleOptions,
          location: {
            lat: place.location?.lat(),
            lng: place.location?.lng(),
          },
        }),
      )
    }

    findPlaceId()
  }, [color, map, postalCode, featureStyleOptions, dispatch])

  if (!postalCodeDetail) {
    return null
  }

  return (
    <CommonMarker
      position={postalCodeDetail.location}
      title={postalCode}
      isActive={false}
      isInitialAnimationRequired={false}
    />
  )
})

const RouteMapBounds: FC = memo(() => {
  const map = useMap()
  const postalCodeStyles = useAppSelector(
    (state) => state.routePlanning.postalCodeStyles,
  )

  useEffect(() => {
    if (!postalCodeStyles || !map) return
    const bounds = new google.maps.LatLngBounds()
    Object.keys(postalCodeStyles).forEach((key) => {
      bounds.extend(new google.maps.LatLng(postalCodeStyles[key].location))
      map.fitBounds(bounds)
    })
  }, [postalCodeStyles, map])
  return null
})

const RouteMapStyleConfig: FC = memo(() => {
  const map = useMap()

  const postalCodeStyles = useAppSelector(
    (state) => state.routePlanning.postalCodeStyles,
  )

  const postalCodeStylesByPlaceId = useMemo(() => {
    const obj: Record<string, google.maps.FeatureStyleOptions> = {}
    Object.values(postalCodeStyles).forEach(({ placeId, style }) => {
      if (placeId) {
        obj[placeId] = style
      }
    })
    return obj
  }, [postalCodeStyles])

  const styleRef = useRef(postalCodeStylesByPlaceId)

  useEffect(() => {
    styleRef.current = postalCodeStylesByPlaceId
  }, [postalCodeStylesByPlaceId])

  useEffect(() => {
    if (!map || !google) return
    const featureLayer = map.getFeatureLayer(
      google.maps.FeatureType.POSTAL_CODE,
    )

    featureLayer.style = (options: any) => {
      const placeId = options.feature.placeId
      return styleRef.current[placeId] || undefined
    }
  }, [map, postalCodeStylesByPlaceId])
  return null
})

const DrawingManager: FC = memo(() => {
  const drawingManager = useDrawingManager()
  const isDrawingEnabled = useAppSelector(drawingModeSelector)

  useEffect(() => {
    if (!drawingManager) return
    if (isDrawingEnabled) {
      drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON) // default mode
      drawingManager.setOptions({ drawingControl: true })
    } else {
      drawingManager.setDrawingMode(null)
      drawingManager.setOptions({ drawingControl: false })
    }
  }, [drawingManager, isDrawingEnabled])

  if (!drawingManager) {
    return null
  }
  return (
    <MapControl position={ControlPosition.TOP_CENTER}>
      <UndoRedoControl drawingManager={drawingManager} />
    </MapControl>
  )
})
